import React from 'react';
import ParsedFormattedMessage from '@utils/miscellaneous/react-intl-utlis';
import { NUMERIC_INPUT_REGEX } from '@utils/validators/regex';
import { ConditionalRendering } from '../conditional-rendering';

interface NumericInputProps {
    label: {
        ariaLabel?: string;
        labelText: string;
        labelClasses?: string[];
        wrapperClasses?: string[];
        isRequired?: boolean;
    };
    input: {
        id: string;
        name: string;
        inputClasses?: string[];
        placeholder?: string;
        type?: string;
        value?: number | string;
        readOnly?: boolean;
        maxLength?: number;
        disabled?: boolean;
        isInputRequired?: boolean;
        min?: number;
        max?: number;
    };
    actions: {
        handleFieldChange: (value: number | string) => void;
        handleFieldBlur?: (value: number | string) => void;
        handleFieldFocus?: (value: number | string) => void;
        handleCustomPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
    };
    labelIcon?: {
        icon?: React.ReactNode;
    };
    children?: React.ReactNode;
    additionalInvalidKeys?: string[]; //to restrict additional keys that are not allowed in the input
}

const NumericInput: React.FC<NumericInputProps> = props => {
    const {
        label: { labelText, labelClasses, wrapperClasses, isRequired, ariaLabel },
        input: {
            id,
            name,
            inputClasses,
            placeholder,
            type = 'number',
            value = '',
            readOnly = false,
            maxLength,
            disabled = false,
            isInputRequired,
            min = Number.MIN_SAFE_INTEGER,
            max = Number.MAX_SAFE_INTEGER,
        },
        actions: { handleFieldChange, handleFieldBlur, handleFieldFocus, handleCustomPaste },
        labelIcon = {},
        children,
        additionalInvalidKeys = [],
    } = props;

    const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;

        // Allow only positive numbers
        if (NUMERIC_INPUT_REGEX.test(inputValue)) {
            const numericValue = Number(inputValue);
            if (numericValue >= min && numericValue <= max) {
                handleFieldChange(numericValue);
            }
        }
    };

    const handleValueBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
        const numericValue = Number(e.target.value);
        handleFieldBlur && handleFieldBlur(numericValue);
    };

    const handleValueFocus = (e: React.ChangeEvent<HTMLInputElement>) => {
        const numericValue = Number(e.target.value);
        handleFieldFocus && handleFieldFocus(numericValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Prevent invalid keys for type='number' (e.g.,'e', 'E', '+')
        const invalidKeys = ['e', 'E', '+', ...additionalInvalidKeys];
        if (invalidKeys.includes(e.key)) {
            e.preventDefault();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        handleCustomPaste && handleCustomPaste(e);
    };

    return (
        <div className={`gi-input ${wrapperClasses && wrapperClasses.join(' ')}`}>
            <ConditionalRendering isTrue={!!labelText || !!labelIcon.icon}>
                <label
                    htmlFor={id}
                    className={`gi-input__label ${labelClasses && labelClasses.join(' ')} required-${isRequired}`}
                    aria-label={ariaLabel}
                >
                    <ParsedFormattedMessage id={labelText} />
                    {labelIcon.icon ? labelIcon.icon : null}
                </label>
            </ConditionalRendering>
            <input
                id={id}
                data-testid={id}
                className={`gi-input__input ${inputClasses && inputClasses.join(' ')}`}
                disabled={disabled}
                name={name}
                type={type}
                value={value || ''}
                readOnly={readOnly}
                aria-required={isInputRequired}
                placeholder={placeholder}
                maxLength={maxLength}
                min={min}
                max={max}
                onKeyDown={handleKeyDown}
                onChange={handleValueChange}
                onBlur={handleValueBlur}
                onFocus={handleValueFocus}
                onPaste={handlePaste}
            />
            {children}
        </div>
    );
};

export default NumericInput;
